import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onContractService } from '../../services/onContractService';
import reportService from '../../services/reportService';
import { useAuth } from '../../context/AuthContext';
import Alert from '../common/Alert';
import {
  FileText,
  Plus,
  Download,
  TrendingUp,
  TrendingDown,
  Eye,
  FileSpreadsheet,
  Pencil,
  Trash2,
} from 'lucide-react';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  StatusBadge,
} from '../common/index.js';

const OnContractList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingAll, setExportingAll] = useState(false);
  const [exportingId, setExportingId] = useState(null);
  const [filterValues, setFilterValues] = useState({ vehicle_id: '' });

  useEffect(() => {
    fetchVehicles();
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [filterValues]);

  const fetchVehicles = async () => {
    try {
      const res = await onContractService.getAllVehicles();
      const data = res.data.vehicles || res.data.data || res.data || [];
      setVehicles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
    }
  };

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const cleanFilters = Object.fromEntries(
        Object.entries(filterValues).filter(([_, v]) => v !== '')
      );
      const res = await onContractService.getAll(cleanFilters);
      const data = res.data.contracts || res.data.data || [];
      setContracts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching contracts:', err);
      Alert.error(err.response?.data?.error || 'Failed to load contracts');
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contractId) => {
    if (!window.confirm('Are you sure you want to delete this contract? This action cannot be undone.')) return;
    try {
      await onContractService.delete(contractId);
      Alert.success('Contract deleted successfully');
      fetchContracts();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete contract');
    }
  };

  const handleExport = async (contractId = null) => {
    try {
      if (contractId) {
        setExportingId(contractId);
      } else {
        setExportingAll(true);
      }
      const params = contractId ? { contract_id: contractId } : {};
      await reportService.downloadOnContractReport(params);
      Alert.success('Excel report downloaded');
    } catch (err) {
      Alert.error('Failed to export report');
    } finally {
      setExportingId(null);
      setExportingAll(false);
    }
  };

  const canEdit = () =>
    user?.role === 'System Admin' || user?.role === 'Admin' || user?.role === 'Accountant';
  const canDelete = () => user?.role === 'System Admin' || user?.role === 'Admin';

  const fmt = (v) => parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  const PLBadge = ({ value }) => {
    const v = parseFloat(value || 0);
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold text-secondary">
        {v >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        ₹{v.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
      </span>
    );
  };

  const BalanceBadge = ({ value }) => {
    const v = parseFloat(value || 0);
    return (
      <StatusBadge
        status={v <= 0 ? 'completed' : 'pending'}
        label={v <= 0 ? 'Cleared' : `₹${fmt(v)}`}
      />
    );
  };

  const columns = [
    {
      key: 'sr',
      label: 'Sr',
      width: '50px',
      render: (_, index) => index + 1,
    },
    {
      key: 'contract_id',
      label: 'Contract ID',
      render: (c) => (
        <button
          type="button"
          onClick={() => navigate(`/on-contracts/view/${c.contract_id}`)}
          className="font-medium text-brand hover:underline"
        >
          {c.contract_id}
        </button>
      ),
    },
    {
      key: 'vehicle',
      label: 'Vehicle',
      render: (c) => (
        <div>
          <div className="font-semibold">{c.vehicle_number}</div>
          <div className="text-xs text-gray-500">{c.vehicle_name}</div>
        </div>
      ),
    },
    {
      key: 'period',
      label: 'Period',
      render: (c) => (
        <div className="text-xs text-gray-600 text-center">
          <div>{c.start_date}</div>
          <div className="text-gray-400">→</div>
          <div>{c.end_date}</div>
        </div>
      ),
    },
    {
      key: 'ml_sheets',
      label: 'ML Sheets',
      render: (c) => c.ml_sheet_ids?.length || 0,
    },
    { key: 'total_shipments', label: 'Shipments' },
    {
      key: 'total_revenue',
      label: 'Revenue (₹)',
      render: (c) => `₹${fmt(c.total_revenue)}`,
    },
    {
      key: 'contract_value',
      label: 'Contract Val (₹)',
      render: (c) => `₹${fmt(c.contract_value)}`,
    },
    {
      key: 'paid_amount',
      label: 'Paid (₹)',
      render: (c) => `₹${fmt(c.paid_amount)}`,
    },
    {
      key: 'balance_amount',
      label: 'Balance',
      render: (c) => <BalanceBadge value={c.balance_amount} />,
    },
    {
      key: 'trip_pl',
      label: 'Trip P&L',
      render: (c) => <PLBadge value={c.trip_pl} />,
    },
  ];

  const getActions = (c) => {
    const actions = [
      {
        label: 'View',
        icon: Eye,
        onClick: () => navigate(`/on-contracts/view/${c.contract_id}`),
      },
      {
        label: exportingId === c.contract_id ? 'Exporting...' : 'Excel',
        icon: FileSpreadsheet,
        onClick: () => handleExport(c.contract_id),
      },
    ];

    if (canEdit()) {
      actions.push({
        label: 'Edit',
        icon: Pencil,
        onClick: () => navigate(`/on-contracts/edit/${c.contract_id}`),
      });
    }

    if (canDelete()) {
      actions.push({
        label: 'Delete',
        icon: Trash2,
        variant: 'danger',
        onClick: () => handleDelete(c.contract_id),
      });
    }

    return actions;
  };

  return (
    <PageLayout
      title="On-Contract Records"
      icon={FileText}
      actions={
        <>
          <Button
            variant="outline"
            icon={Download}
            onClick={() => handleExport(null)}
            disabled={exportingAll || contracts.length === 0}
          >
            {exportingAll ? 'Exporting...' : 'Export All'}
          </Button>
          {canEdit() && (
            <Button icon={Plus} onClick={() => navigate('/on-contracts/add')}>
              Add Contract
            </Button>
          )}
        </>
      }
    >
      <FilterPanel
        filters={[
          {
            key: 'vehicle_id',
            label: 'Vehicle',
            type: 'select',
            options: vehicles.map((v) => ({
              value: v.id,
              label: `${v.vehicle_number} — ${v.vehicle_name}`,
            })),
          },
        ]}
        values={filterValues}
        onChange={setFilterValues}
        onClear={() => setFilterValues({ vehicle_id: '' })}
      />

      <DataTable
        columns={columns}
        data={contracts}
        loading={loading}
        emptyMessage="No contracts found"
        rowKey="id"
        actions={getActions}
      />
    </PageLayout>
  );
};

export default OnContractList;
